<div class="flex flex-col expert-container gap-2">
  <div class="flex gap-4 h-full w-full min-h-full min-w-full">
    <div
      class="flex flex-col gap-4 justify-start content-center"
      style="min-width: 212px"
    >
      <ng-container [ngTemplateOutlet]="expertInfoTmpl"></ng-container>
      <ng-container [ngTemplateOutlet]="expertTimelineTmpl"></ng-container>
      @if (isExpertApprovalRequired) {
        <ng-container
          [ngTemplateOutlet]="expertApprovalActionTmpl"
        ></ng-container>
      }
      @if (smallScreen) {
        <ng-container [ngTemplateOutlet]="expertActionsTmpl"></ng-container>
      }

      <ng-container
        [ngTemplateOutlet]="expertSheduledActionTmpl"
      ></ng-container>
    </div>

    <div class="flex grow" style="min-width: 0">
      <ng-container [ngTemplateOutlet]="expertDetailsTmpl"></ng-container>
    </div>

    @if (!smallScreen && !collapseActions()) {
      <div
        class="flex flex-col gap-4 justify-start content-end"
        style="min-width: 400px"
        data-test-id="expert-actions"
      >
        <ng-container [ngTemplateOutlet]="expertActionsTmpl"></ng-container>
      </div>
    }

    @if (!smallScreen && collapseActions()) {
      <ng-container [ngTemplateOutlet]="collapsedExpertActionsTemplate" />
    }
  </div>
</div>

<ng-template #expertInfoTmpl>
  <div class="expert-icon">
    <app-expert-hexagon
      [viewStage]="expertIcon"
      [tagline]="expertData.tagline"
    ></app-expert-hexagon>
  </div>
</ng-template>

<ng-template #expertTimelineTmpl>
  <app-expert-timeline
    [expert]="expertData"
    [viewStage]="viewStage"
  ></app-expert-timeline>
</ng-template>

<ng-template #expertApprovalActionTmpl>
  <div>
    @if (['new', 'accepted'].includes(viewStage) && !isCallInProgress) {
      <button
        mat-flat-button
        color="primary"
        class="text-span"
        data-test-id="expert-toggle-approval-button"
        (click)="toggleApproval(expertData)"
      >
        <mat-icon
          class="secondaryColor"
          data-test-id="expert-toggle-approval-button-state-icon"
        >
          {{ expertData.clientApproved ? 'lock' : 'lock_open' }}
        </mat-icon>
        <span data-test-id="expert-toggle-approval-button-text">
          {{
            expertData.clientApproved ? 'Withdraw approval' : 'Approve expert'
          }}
        </span>
      </button>
    }
  </div>
</ng-template>

<ng-template #expertDetailsTmpl>
  <div class="mat-group-custom-style flex grow" style="min-width: 0">
    <mat-tab-group
      #expertTabGroup
      mat-stretch-tabs="false"
      color="primary"
      class="flex grow"
      style="min-width: 0"
      dynamicHeight
      [selectedIndex]="0"
      (selectedTabChange)="onTabChange($event)"
    >
      @if (expertData.echoSummary) {
        <mat-tab [label]="ExpertTab.CallSummary">
          <ng-template mat-tab-label>
            <div
              class="flex flex-row justify-start items-center gap-2"
              data-test-id="expert-call-summary-tab"
            >
              <mat-icon class="tab-icon">speaker_notes</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Call summary
              </span>
            </div>
          </ng-template>
          <app-echo [fileKey]="expertData.echoSummary.fileKey"></app-echo>
        </mat-tab>
      }

      @if (expertData.echoSummary) {
        <mat-tab [label]="ExpertTab.SavedQuotes">
          <ng-template mat-tab-label>
            <div
              class="flex flex-row justify-start items-center gap-2"
              data-test-id="expert-saved-quotes-tab"
            >
              <mat-icon class="tab-icon">format_quote</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Saved quotes
              </span>
            </div>
          </ng-template>
          <app-expert-quotes
            [expertId]="expertData.expertId"
            [calls]="calls"
            (viewTranscript)="onViewTranscript($event)"
          ></app-expert-quotes>
        </mat-tab>
      }

      @if (expertData.screenerResponse) {
        <mat-tab [label]="ExpertTab.Screener">
          <ng-template mat-tab-label>
            <div
              class="flex gap-2 justify-start items-center"
              data-test-id="expert-screener-tab"
            >
              <mat-icon class="tab-icon">fact_check</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Screener
              </span>
            </div>
          </ng-template>
          <app-expert-text
            data-test-id="expert-screener-response"
            [text]="expertData.screenerResponse"
          >
          </app-expert-text>
        </mat-tab>
      }

      <mat-tab [label]="ExpertTab.Biography">
        <ng-template mat-tab-label>
          <div
            class="flex gap-2 justify-start items-center"
            data-test-id="expert-biography-tab"
          >
            <mat-icon class="tab-icon">person_pin_outline</mat-icon>
            <span class="expert-header-title" data-test-id="expert-tab-label">
              Biography
            </span>
          </div>
        </ng-template>

        @if (expertData.highlightNote) {
          <app-expert-text
            data-test-id="expert-highlight-note-text"
            [text]="expertData.highlightNote"
          >
          </app-expert-text>
        }

        @if (!expertData.bio) {
          <div style="margin: 8px 0">
            @if (!expertData.requestedBio) {
              <button
                mat-stroked-button
                color="primary"
                data-test-id="expert-biography-request-biography-button"
                (click)="requestBio()"
              >
                Request expert bio
              </button>
            } @else {
              <span data-test-id="expert-biography-biography-requested-text">
                We're gathering data for your expert
              </span>
            }
          </div>
        } @else {
          <app-expert-text
            data-test-id="expert-biography-text"
            [text]="expertData.bio"
          >
          </app-expert-text>
        }
      </mat-tab>

      @if (viewStage !== 'completed') {
        <mat-tab [label]="ExpertTab.Availability">
          <ng-template mat-tab-label>
            <div
              class="flex gap-2 justify-start items-center"
              data-test-id="expert-availabiliy-tab"
            >
              <mat-icon class="tab-icon">event_available</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Availability
              </span>
            </div>
          </ng-template>
          <!-- Lazy, so the calendar is laid out once it's in view -->
          <ng-template matTabContent>
            <ng-container [ngTemplateOutlet]="expertAvailabilityTemplate" />
          </ng-template>
        </mat-tab>
      }

      @if (expertData.followUpQuestionsResponse) {
        <mat-tab [label]="ExpertTab.FollowUpQuestions">
          <ng-template mat-tab-label>
            <div
              class="flex gap-2 justify-start items-center"
              data-test-id="expert-follow-up-questions-tab"
            >
              <mat-icon class="tab-icon">forum</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Follow-up questions
              </span>
            </div>
          </ng-template>
          <app-expert-text
            data-test-id="expert-follow-up-questions-text"
            [text]="expertData.followUpQuestionsResponse"
          >
          </app-expert-text>
        </mat-tab>
      }

      <mat-tab [label]="ExpertTab.Comments">
        <ng-template mat-tab-label>
          <div
            class="flex gap-2 justify-start items-center"
            data-test-id="expert-comments-tab"
          >
            <mat-icon class="tab-icon">comment</mat-icon>
            <span class="expert-header-title" data-test-id="expert-tab-label">
              Comments
            </span>
          </div>
        </ng-template>

        <div class="expert-comments flex flex-col gap-3">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Comments</mat-label>
            <textarea
              #clientNotes="ngModel"
              matInput
              data-test-id="expert-comments-text-area"
              [(ngModel)]="expertData.clientNotes"
            ></textarea>
          </mat-form-field>
          <div>
            <button
              mat-flat-button
              color="primary"
              data-test-id="expert-save-comments"
              [disabled]="clientNotes.pristine"
              (click)="saveClientNotes(clientNotes.value)"
            >
              Save Comment
            </button>
          </div>
        </div>
      </mat-tab>

      @if (viewStage === 'declined') {
        <mat-tab [label]="ExpertTab.RejectionReason">
          <ng-template mat-tab-label>
            <div
              class="flex gap-2 justify-start items-center"
              data-test-id="expert-rejection-reason-tab"
            >
              <mat-icon class="tab-icon">thumb_down</mat-icon>
              <span class="expert-header-title" data-test-id="expert-tab-label">
                Rejection reason
              </span>
            </div>
          </ng-template>
          <div class="expert-comments flex flex-col gap-3">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Rejection reason</mat-label>
              <textarea
                readonly
                matInput
                data-test-id="expert-rejection-reason-text-area"
                [ngModel]="expertData.rejectionRecord?.message"
              ></textarea>
            </mat-form-field>
          </div>
        </mat-tab>
      }

      @if (smallScreen) {
        <mat-tab [label]="ExpertTab.Downloads">
          <ng-template mat-tab-label>
            <div
              class="flex gap-2 justify-start items-center"
              data-test-id="expert-downloads-tab"
            >
              <mat-icon class="tab-icon">download</mat-icon>
              <span
                class="expert-header-title"
                data-test-id="expert-downloads-tab-label"
              >
                Downloads
              </span>
            </div>
          </ng-template>
          <div class="flex flex-col gap-8 justify-start content-end">
            <app-expert-files
              class="full-width"
              style="padding-top: 16px"
              [files]="expertData.uploadedFiles"
              [filePrefix]="expertFilePrefix"
              [actions]="expertData.actions"
              (replayInterview)="viewCallTranscript($event)"
            >
            </app-expert-files>
          </div>
        </mat-tab>
      }
    </mat-tab-group>
  </div>
</ng-template>

<ng-template #expertActionsTmpl>
  <div class="expert-header flex gap-2 justify-start items-center">
    @if (!smallScreen) {
      <div class="flex grow gap-2 justify-start items-center">
        <mat-icon>download</mat-icon>
        <span class="expert-header-title">Downloads</span>
      </div>

      @if (calls.length === 1) {
        <button
          class="replay-interview-link"
          data-test-id="expert-view-call-transcript-button"
          (click)="viewCallTranscript()"
        >
          Replay interview
        </button>
      }
    }

    <ng-container [ngTemplateOutlet]="expertOnHoldImpl"></ng-container>
    <ng-container [ngTemplateOutlet]="expertRejectTmpl"></ng-container>
    @if (!smallScreen) {
      <button
        mat-icon-button
        data-test-id="expert-collapse-actions"
        aria-label="Collapse actions"
        (click)="toggleCollapseActions()"
      >
        <mat-icon>last_page</mat-icon>
      </button>
    }
  </div>

  @if (!smallScreen) {
    <app-expert-files
      class="full-width"
      [files]="expertData.uploadedFiles"
      [filePrefix]="expertFilePrefix"
      [actions]="expertData.actions"
      (replayInterview)="viewCallTranscript($event)"
    >
    </app-expert-files>
  }
</ng-template>

<ng-template #collapsedExpertActionsTemplate>
  <mat-divider [vertical]="true" />
  <div class="flex flex-col gap-2">
    @let fileCount = expertData.uploadedFiles.length;
    <button
      mat-icon-button
      class="font-grey"
      data-test-id="expert-expand-actions"
      [matTooltip]="`Show downloads (${fileCount})`"
      (click)="toggleCollapseActions()"
    >
      <mat-icon
        class="downloads-badge"
        aria-hidden="false"
        matBadgeSize="small"
        [matBadge]="fileCount"
      >
        download
      </mat-icon>
    </button>

    @if (calls.length === 1) {
      <button
        mat-icon-button
        class="replay-interview-link"
        matTooltip="Replay interview"
        aria-label="Replay interview"
        data-test-id="expert-view-call-transcript-button"
        (click)="viewCallTranscript()"
      >
        <mat-icon>replay</mat-icon>
      </button>
    }

    <ng-container [ngTemplateOutlet]="expertOnHoldImpl"></ng-container>
    <ng-container [ngTemplateOutlet]="expertRejectTmpl"></ng-container>
  </div>
</ng-template>

<ng-template #expertRejectTmpl>
  <div>
    @if (
      (!expertData.callTime && viewStage === 'new') || viewStage === 'declined'
    ) {
      <button
        mat-icon-button
        class="font-grey"
        data-test-id="expert-reject-expert"
        [disabled]="!canRejectExperts"
        [matTooltip]="
          expertData.clientRejected ? 'Unreject expert' : 'Reject expert'
        "
        (click)="
          toggleRejectExpert(!expertData.clientRejected, expertData.expertId)
        "
      >
        <mat-icon>
          {{ expertData.clientRejected ? 'undo' : 'thumb_down' }}
        </mat-icon>
      </button>
    }
  </div>
</ng-template>

<ng-template #expertOnHoldImpl>
  <div>
    @if (viewStage === 'new') {
      <button
        mat-icon-button
        class="font-grey hold"
        data-test-id="expert-set-hold-status"
        [matTooltip]="
          expertData.onHold ? 'Remove expert from hold' : 'Place expert on hold'
        "
        (click)="toggleOnHold(!expertData.onHold, expertData.expertId)"
      >
        @if (!expertData.onHold) {
          <app-on-hold-svg colour="#b4becb" [large]="true"> </app-on-hold-svg>
        } @else {
          <mat-icon>undo</mat-icon>
        }
      </button>
    }
  </div>
</ng-template>

<ng-template #expertSheduledActionTmpl>
  @if (
    (viewStage === 'accepted' || viewStage === 'completed') && isCallInProgress
  ) {
    <button
      mat-flat-button
      color="primary"
      class="text-span"
      data-test-id="expert-report-no-show-button"
      (click)="expertNoShow(expertData)"
    >
      Report expert no show
    </button>
  }

  @if (
    viewStage === 'completed' ||
    (opportunity &&
      ['To be invoiced', 'Closed Won', 'Closed Lost'].includes(
        opportunity.stageName
      ))
  ) {
    <button
      mat-flat-button
      color="primary"
      class="text-span"
      data-test-id="expert-speak-again-button"
      (click)="requestExpertEngagement(expertData)"
    >
      {{
        viewStage === 'completed'
          ? 'Speak to this expert again'
          : 'Speak to this expert'
      }}
    </button>
  }

  @if (requestNewEngagementSent) {
    <div>Your request for new engagement has been received</div>
  }

  <button
    mat-flat-button
    color="primary"
    class="text-span"
    data-test-id="expert-share-expert"
    (click)="shareExpert(expertData)"
  >
    Share expert
  </button>

  @if (viewStage === 'completed') {
    <button
      mat-flat-button
      color="primary"
      class="text-span"
      data-test-id="expert-ask-follow-up-question-button"
      (click)="askFollowUpQuestions()"
    >
      Ask follow-up questions
    </button>
  }
</ng-template>

<ng-template #expertAvailabilityTemplate>
  <app-expert-availability
    [expert]="expertData"
    [opportunityId]="opportunity.opportunityId"
    [selectable]="viewStage === 'new'"
    [selecting]="isAcceptAvailabilityInProgress"
    [pendingTime]="pendingAvailability"
    (selectTime)="acceptAvailability($event.range, $event.overlappingCalls)"
  />
  @if (viewStage === 'new') {
    <div class="request-availability">
      <div class="flex flex-row gap-3 items-center">
        <mat-icon
          class="request-availability-icon"
          [class.requested]="expertData.requestedAnotherTime"
          [matTooltip]="additionalAvailabilityRequestText.title"
          [matTooltipDisabled]="!expertData.requestedAnotherTime"
        >
          more_time
        </mat-icon>
        <div class="flex flex-col">
          <strong class="request-availability-title">
            {{ additionalAvailabilityRequestText.title }}
          </strong>
          <span class="request-availability-detail">
            {{ additionalAvailabilityRequestText.description }}
          </span>
        </div>
      </div>
      <button
        mat-stroked-button
        class="request-availability-button"
        data-test-id="expert-request-consultation-time"
        [matTooltip]="additionalAvailabilityRequestText.title"
        [matTooltipDisabled]="!expertData.requestedAnotherTime"
        (click)="requestDifferentTime(expertData)"
      >
        @if (isExpertApprovalRequired && !expertData.clientApproved) {
          <mat-icon data-test-id="expert-request-consultation-time-locked">
            lock
          </mat-icon>
        }
        Request additional availability
      </button>
    </div>
  }
</ng-template>
